import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconClockDollar, IconSearch } from "@tabler/icons-react";
import { Badge, Button, Group, Input, PageHeader, PaginadorTabla, Paper, Stack, TablaSkeleton, Table, Tbody, Td, Text, Th, Thead, Tr, type ColumnaSkeleton } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { formatFecha } from "@/shared/format/fecha";
import { useEnEsperaCfdi } from "@/features/en-espera-cfdi/hooks/use-en-espera-cfdi";
import type { SolicitudEnEsperaDeCfdi } from "@/features/en-espera-cfdi/services/en-espera-cfdi.api";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";

const PAGE_SIZE = 15;

/** Columnas de la tabla (Folio · Organización · Proveedor · Pago · Límite · Estado · Acción) para el skeleton. */
const COLUMNAS_SKELETON: ColumnaSkeleton[] = [{ lineas: 2 }, {}, {}, {}, {}, {}, {}];

function diasParaVencer(fechaLimite: string): number {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const limite = new Date(fechaLimite);
  limite.setHours(0, 0, 0, 0);
  return Math.round((limite.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
}

function SemaforoEspera({ solicitud }: { solicitud: SolicitudEnEsperaDeCfdi }) {
  if (solicitud.esperaCfdiVencida) {
    return <Badge color="peligro" size="sm">Vencida</Badge>;
  }
  const dias = diasParaVencer(solicitud.esperaCfdiFechaLimite);
  if (dias <= 5) {
    return <Badge color="advertencia" size="sm">Vence en {dias}d</Badge>;
  }
  return <Badge color="slate" size="sm">Vigente · {dias}d</Badge>;
}

/**
 * P44 · FB60 Contrapago · bandeja de solicitudes Autorizadas que aún esperan
 * el CFDI real (llega después del pago). Hotspots 86/87 · la ruta y el endpoint
 * exigen `contrapagos.gestionar`; el backend acota: quien tiene además
 * `solicitudes.ver_todas` (Mesa de Control) ve todas, el resto solo las suyas.
 * «Adjuntar CFDI» solo aparece en las filas PROPIAS (conciliar es del dueño).
 */
export function EnEsperaCfdiPage() {
  const navigate = useNavigate();
  const puedeConciliar = useUserPermisos().includes(PERMISOS.contrapagosGestionar);
  const [folio, setFolio] = useState("");
  const [page, setPage] = useState(1);
  const [folioDebounced] = useDebouncedValue(folio, 350);
  useEffect(() => setPage(1), [folioDebounced]);

  const { data, isFetching } = useEnEsperaCfdi({
    folio: folioDebounced.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title="Contrapagos pendientes"
        description="FB60 Contrapago · solicitudes ya autorizadas (y pagadas) en espera del CFDI real."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Contrapagos pendientes" },
        ]}
      />
      <Paper padding="lg">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Input
              placeholder="Buscar por folio"
              leftSection={<IconSearch size={16} />}
              value={folio}
              onChange={(e) => setFolio(e.currentTarget.value)}
              wrapperClassName="w-70"
            />
            {data && (
              <PaginadorTabla
                total={data.total}
                page={data.page}
                pageSize={data.pageSize}
                onPageChange={setPage}
                sustantivo="solicitudes"
                variante="superior"
              />
            )}
          </Group>
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm" horizontalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Folio</Th>
                  <Th>Organización</Th>
                  <Th>Proveedor</Th>
                  <Th>Fecha de pago</Th>
                  <Th>Límite CFDI</Th>
                  <Th>Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              {/* Primera carga: filas skeleton bajo el encabezado real (no una tabla vacía). */}
              {!data ? (
                <TablaSkeleton columnas={COLUMNAS_SKELETON} filas={PAGE_SIZE} dentroDeTabla etiqueta="Cargando contrapagos…" />
              ) : (
              <Tbody>
                {data.items.map((s) => (
                  <Tr key={s.id}>
                    <Td>
                      <Text size="sm" fw={600}>#{s.numeroSolicitud}</Text>
                      <Text size="xs" c="dimmed">{s.folio}</Text>
                    </Td>
                    <Td>{s.organizacionNombre ?? "—"}</Td>
                    <Td>
                      {s.proveedorRazonSocial
                        ? `#${s.proveedorNumero} · ${s.proveedorRazonSocial}`
                        : "—"}
                    </Td>
                    <Td>{formatFecha(s.fechaPago)}</Td>
                    <Td>{formatFecha(s.esperaCfdiFechaLimite)}</Td>
                    <Td><SemaforoEspera solicitud={s} /></Td>
                    <Td>
                      {puedeConciliar && s.esPropia ? (
                      <Button
                        size="xs"
                        leftSection={<IconClockDollar size={14} />}
                        onClick={() =>
                          navigate({
                            to: "/solicitudes/$id/conciliar-cfdi",
                            params: { id: s.id },
                          })
                        }
                      >
                        Adjuntar CFDI
                      </Button>
                      ) : (
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => navigate({ to: "/solicitudes/$id", params: { id: s.id } })}
                        >
                          Ver detalle
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
              )}
            </Table>
            {!isFetching && data?.items.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" className="py-5">
                Sin solicitudes Contrapago en espera de CFDI.
              </Text>
            )}
          </div>
          {data && (
            <PaginadorTabla
              total={data.total}
              page={data.page}
              pageSize={data.pageSize}
              onPageChange={setPage}
              sustantivo="solicitudes"
            />
          )}
        </Stack>
      </Paper>
    </div>
  );
}
